import { EchartsWithTheme, Button, Empty, Modal, Radio, message, useTheme } from '@maxtropy/components';
import { CompareType, CompareTypeDisplay, DateType, MeasurementType } from '../utils';
import { getBasicChartOptionByHis, getBasicChartOptionByObj } from './chartOption';
import DeviceCompare, { DeviceMonitorSingleValue, DeviceMonitorValue } from './components/DeviceCompare';
import IndicatorCompare from './components/IndicatorCompare';
import React, { useCallback, useEffect, useState } from 'react';
import ResizeObserver from 'rc-resize-observer';
import { RadioChangeEvent, Spin } from 'antd';
import { isEmpty } from 'lodash-es';
import styles from './index.module.scss';
import { EnergyMonitorContext } from '../';
import DateSwitch from './components/DateSwitch';
import {
  apiV2HighEnergyDeviceV2DeviceComparePost,
  V2HighEnergyDeviceV2DeviceComparePostResponse,
  apiV2HighEnergyDeviceV2IndicatorComparePost,
  V2HighEnergyDeviceV2IndicatorComparePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import useLatestRequest from '../useLatestRequest';

export type DeviceDataProps = Exclude<V2HighEnergyDeviceV2DeviceComparePostResponse['list'], undefined>[number];
export type IndicatorProps = Exclude<V2HighEnergyDeviceV2IndicatorComparePostResponse['list'], undefined>[number];
interface Prop {
  setOpen: (value: boolean) => void;
}
const ModalCompare = ({ setOpen }: Prop) => {
  const [objChartData, setObjChartData] = useState<DeviceDataProps[]>([]); // 对象对比图表数据
  const [hisChartData, setHisChartData] = useState<IndicatorProps[]>([]); // 历史对比图表数据
  const [compareType, setCompareType] = useState<CompareType>(CompareType.DEVICE);
  const [timeResolution, setTimeResolution] = useState<any>(DateType.DAY);
  const { deviceLedgerId } = React.useContext(EnergyMonitorContext);
  const [dateRange, setDateRange] = useState<any>([]);
  const [deviceMonitor, setDeviceMonitor] = useState<DeviceMonitorValue>();
  const [deviceCheck, setDeviceCheck] = useState<number[]>();
  const [dataQueryPeriodId, setDataQueryPeriodId] = useState<number | undefined>();
  const [isDeviceperiod, setIsDeviceperiod] = useState<boolean>(false);
  const [isIndicatorperiod, setIsIndicatorperiod] = useState<boolean>(false);
  const [deviceChange, setDeviceChange] = useState<DeviceMonitorSingleValue[]>();
  const [indicatorChange, setIndicatorChange] = useState<DeviceMonitorSingleValue[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const theme = useTheme();
  const { runLatest: runLatestDevice, cancelLatest: cancelLatestDevice } = useLatestRequest();
  const { runLatest: runLatestIndicator, cancelLatest: cancelLatestIndicator } = useLatestRequest();
  const onCompareTypeChange = (e: RadioChangeEvent) => {
    setLoading(false);
    setCompareType(e.target.value);
    if (e.target.value === CompareType.DEVICE) {
      cancelLatestIndicator();
      setIsIndicatorperiod(false);
      setHisChartData([]);
      setIndicatorChange(undefined);
    } else {
      cancelLatestDevice();
      setIsDeviceperiod(false);
      setDeviceMonitor(undefined);
      setDeviceChange(undefined);
      setObjChartData([]);
    }
    setDataQueryPeriodId(undefined);
  };

  const onChangeData = useCallback(
    (value: { timeResolution: string; fromAndTo: number[]; dataQueryPeriodId?: number | undefined }) => {
      setDateRange(value.fromAndTo);
      setTimeResolution(value.timeResolution);
      setDataQueryPeriodId(value.dataQueryPeriodId);
    },
    []
  );

  // 设备
  const OnDeviceSubmission = () => {
    if (!deviceChange || !deviceCheck || !dateRange) return;

    setLoading(true);
    runLatestDevice(
      () =>
        apiV2HighEnergyDeviceV2DeviceComparePost({
          timeResolution,
          inventoryIds: deviceCheck,
          startTime: dateRange[0],
          endTime: dateRange[1],
          compareItem: deviceChange,
          dataQueryPeriodId,
        }),
      {
        onSuccess: res => {
          setObjChartData(res.list ?? []);
          setIsDeviceperiod(deviceChange?.[0].measurementType === MeasurementType.ACCUM ? true : false);
          setDeviceMonitor(deviceChange[0]);
        },
        onSettled: () => setLoading(false),
      }
    );
  };

  const OnIndexSubmission = () => {
    if (isEmpty(indicatorChange)) {
      message.error({
        content: '至少需要选择一个选项。',
      });
      return;
    }
    if (!indicatorChange || !deviceLedgerId || !dateRange) return;
    setLoading(true);
    runLatestIndicator(
      () =>
        apiV2HighEnergyDeviceV2IndicatorComparePost({
          timeResolution,
          dataQueryPeriodId,
          inventoryId: deviceLedgerId,
          startTime: dateRange[0],
          endTime: dateRange[1],
          compareItem: indicatorChange,
        }),
      {
        onSuccess: res => {
          setHisChartData(res.list ?? []);
          const period = indicatorChange.every(item => {
            return item.measurementType === MeasurementType.ACCUM;
          });
          setIsIndicatorperiod(indicatorChange.length === 0 ? false : period);
        },
        onSettled: () => setLoading(false),
      }
    );
  };

  useEffect(() => {
    if (!indicatorChange || !deviceLedgerId || !dateRange) return;
    OnIndexSubmission();
  }, [timeResolution, dataQueryPeriodId, dateRange]);

  useEffect(() => {
    if (!deviceChange || !deviceCheck || !dateRange) return;
    OnDeviceSubmission();
  }, [timeResolution, dataQueryPeriodId, dateRange]);
  return (
    <Modal
      open
      onCancel={() => {
        setOpen(false);
      }}
      title="数据对比"
      size="big"
      contentClassName={styles.comparedModal}
      footer={[
        <Button key="close" onClick={() => setOpen(false)}>
          关闭
        </Button>,
      ]}
    >
      <div className={styles.leftCol}>
        <Radio.Group
          options={Object.entries(CompareTypeDisplay).map(([k, v]) => ({
            label: v,
            value: k,
          }))}
          onChange={e => {
            onCompareTypeChange(e);
          }}
          value={compareType}
          optionType="button"
          buttonStyle="solid"
        />
        {compareType === CompareType.DEVICE && (
          <>
            <DeviceCompare onDeviceChange={e => setDeviceCheck(e)} onDeviceMonitorChange={e => setDeviceChange(e)} />
          </>
        )}
        {compareType === CompareType.INDICATOR && <IndicatorCompare onIndicatorChange={e => setIndicatorChange(e)} />}
        <div className={styles.btnArea}>
          <Button
            wrapStyle={{ width: '100%' }}
            style={{ width: '100%' }}
            type="primary"
            onClick={() => {
              compareType === CompareType.DEVICE ? OnDeviceSubmission() : OnIndexSubmission();
            }}
          >
            对比
          </Button>
        </div>
      </div>
      <div className={styles.rightCol}>
        <p>对比预览</p>
        <div className={styles.dateSwitch}>
          <DateSwitch
            onQueryChange={onChangeData}
            isDeviceperiod={isDeviceperiod}
            isIndicatorperiod={isIndicatorperiod}
            compareType={compareType}
          />
        </div>
        <div className={styles.chartArea} style={{ marginTop: 12 }}>
          {!isEmpty(objChartData) || !isEmpty(hisChartData) ? (
            <ResizeObserver>
              <Spin spinning={loading}>
                <EchartsWithTheme
                  option={
                    compareType === CompareType.DEVICE
                      ? getBasicChartOptionByObj(objChartData, timeResolution, deviceMonitor)
                      : getBasicChartOptionByHis(hisChartData, timeResolution)
                  }
                  theme={theme}
                  style={{ height: 480 }}
                />
              </Spin>
            </ResizeObserver>
          ) : (
            <Empty style={{ marginTop: 80 }} />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ModalCompare;
